import FormBuilderModel from "../../../../common/models/tracked/formBuilderModel";
import {PaymentLineItemType} from "./paymentLineItemType";
import ConditionalRuleSetFactory from "../../../../common/models/conditionalRules/conditionalRuleSetFactory";
import ConditionalRuleSet from "../../../../common/models/conditionalRules/conditionalRuleSet";
import IConditionalRuleContainer from "../../../../common/models/conditionalRules/conditionalRuleContainer";
import PaymentLineItemAmountFactory from "./paymentLineItemAmountFactory";
import PaymentLineItemAmount from "./paymentLineItemAmount";
import {INavigableValidatableModel} from "../../../../common/interfaces/navigableValidationInterfaces";
import WorkflowStep from "../workflow/workflowStep";
import Validatable from "../../../../common/mixins/validatable";
import PaymentLineItemValidation from "../../../../common/models/navigableValidations/paymentLineItemValidation";
import {RelationType} from "../../../../common/models/relationType";
import IRepo from "../../../../common/interfaces/repo";
import Repo from "../../../../common/state/modules/repo";
import FormElement from "../formElements/formElement";
import {Capability} from "../formElements/formElementCapability";
import FormElementConditionalFields from "../conditionalRules/formElementConditionalFields";
import FormElementConditionalRuleSet from "../conditionalRules/formElementConditionalRuleSet";
import {ConditionalFieldDefinition} from "../../../../common/models/conditionalRules/conditionalFieldDefinition";
import {ObjectHelper} from "~/helpers/objectHelper";
import pluralizeable from "~/mixins/pluralizeable";

export default class PaymentLineItem extends Validatable(FormBuilderModel) implements IConditionalRuleContainer, INavigableValidatableModel<WorkflowStep> {
  name: string = null;
  item_type: PaymentLineItemType = PaymentLineItemType.Fee;
  sku: string = null;
  description: string = null;
  sort_order: number;
  except = ['form', 'amount_conditional_rule_sets', 'amount'];
  amount: PaymentLineItemAmount;
  amount_formula: string;

  static repo: IRepo<PaymentLineItem> = new Repo<PaymentLineItem>('paymentLineItems', PaymentLineItem);

  createEmbeddedObject = {
    amount_conditional_rule_sets: (parent, json) => {
      const definition = FormElementConditionalFields.PAYMENT_LINE_ITEM_AMOUNT;
      return ConditionalRuleSetFactory.createForContainer(parent, definition, json, true, false);
    },
    amount: (parent, json) => {
      return PaymentLineItemAmountFactory.create(parent);
    }
  };
  amount_conditional_rule_sets = {};

  //******************** VALIDATABLE ********************//
  _validation: PaymentLineItemValidation = new PaymentLineItemValidation(this);

  getDisplayName(propName: string, value?): string {
    switch (propName) {
      default:
        return propName;
        // return this.attributes_metadata[propName].display_name;
    }
  }
  //******************** VALIDATABLE END ********************//

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    return {
      'amount_conditional_rule_sets': { type: ConditionalRuleSet, relationType: RelationType.dictionary },

      'amount': { type: PaymentLineItemAmount, relationType: RelationType.singleObject }
    };
  }

  get EMBEDDED_HASHES(): string[] {
    return ['amount'];
  }

  get rootName(): string {
    return 'payment_line_items';
  }

  get elementName(): string {
    return 'payment_line_item';
  }

  get isComplete(): boolean {
    return this.name && this.name !== '' && this.sku && this.sku !== '';
  }

  getConditionalRuleSets(fieldName: string): { [key: string]: FormElementConditionalRuleSet } {
    return this[`${fieldName}_conditional_rule_sets`];
  }

  getAmountConditionalRuleSets(): { [key: string]: FormElementConditionalRuleSet } {
    return this.getConditionalRuleSets('amount');
  }

  getSortedConditionalRuleSets(fieldName: string): FormElementConditionalRuleSet[] {
    const ruleSets = this.getConditionalRuleSets(fieldName);
    return Object.keys(this.getConditionalRuleSets(fieldName)).map(key => ruleSets[key])
      .sort((x, y) => x.sort_order < y.sort_order ? -1 : 1);
  }

  nextRuleSetSortOrder(fieldName: string): number {
    let position = 0;
    Object.keys(this.getConditionalRuleSets(fieldName))
        .map(key => this.getConditionalRuleSets(fieldName)[key])
        .forEach((element, i) => {
          if (i >= position) {
            position = i + 1;
          }
        });
    return position;
  }

  getPossibleAmounts(): Array<number | string> {
    const ruleSets = this.getAmountConditionalRuleSets();
    const amounts = [this.amount_formula || this.amount.payment_amount];
    Object.keys(ruleSets).map(k => amounts.push(ruleSets[k].conditional_value_formula || ruleSets[k].conditional_value.payment_amount));
    return amounts;
  }

  toString(): string {
    const possibleAmounts = this.getPossibleAmounts();
    const numericAmounts = possibleAmounts.filter(amount => typeof amount === 'number') as number[];
    const formulaAmounts = possibleAmounts.filter(amount => typeof amount === 'string') as number[];
    const min = Math.min(...numericAmounts);
    const max = Math.max(...numericAmounts);
    let str = this.name;
    if (min !== Infinity) {
      str += ` $${min/100.0}`;
      if (max !== -Infinity && min !== max) str += ` - $${max/100.0}`;
    }
    if (formulaAmounts.length) {
      str += `, ${pluralizeable.filters.pluralize('formula', formulaAmounts.length)}`
    }
    return str;
  }

  createNewRuleSet(fieldName: string, definition: ConditionalFieldDefinition): FormElementConditionalRuleSet {
    return new FormElementConditionalRuleSet(fieldName, definition);
  }

  getReferencedControl(id: string): FormElement {
    return FormElement.repo.find(id);
  }

  getReferencableControls(): FormElement[] {
    return this.rootModel.referenceableFormElementsFor(null);
  }
}